import { formatDistanceToNow } from 'date-fns';
import { PanelRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLead } from '@/hooks/useLeads';
import { useLeadActivities } from '@/hooks/useLeadActivities';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import type { ConversationThread } from '@/types/omniChannel';
import type { BadgeProps } from '@/components/ui/badge';

interface ThreadContextPanelProps {
  thread: ConversationThread | null;
}

const STATUS_VARIANTS: Record<string, BadgeProps['variant']> = {
  new: 'info',
  needs_follow_up: 'warning',
  converted: 'success',
  lost: 'destructive-soft',
};

function formatStatus(status: string): string {
  const label = status.replace(/_/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {children}
    </p>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-[13px] text-foreground" title={value}>
        {value}
      </span>
    </div>
  );
}

export function ThreadContextPanel({ thread }: ThreadContextPanelProps) {
  const leadId = thread?.lead_id || null;
  const { data: lead, isLoading: leadLoading } = useLead(leadId);
  const { data: activities, isLoading: activitiesLoading } = useLeadActivities(leadId);
  const { formatMoney: formatCurrency } = useCompanyLocale();

  if (!thread) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-card p-4 text-center text-muted-foreground">
        <PanelRight className="h-6 w-6 opacity-50" />
        <p className="text-xs">Conversation details appear here</p>
      </div>
    );
  }

  const serviceSuburb = [lead?.service_types?.name, lead?.customer_suburb]
    .filter(Boolean)
    .join(' · ');

  return (
    <ScrollArea className="h-full min-h-0 flex-1 bg-card">
      <div className="space-y-5 p-4">
        {/* Linked lead */}
        <section className="space-y-2">
          <SectionEyebrow>Linked lead</SectionEyebrow>
          {!leadId ? (
            <p className="text-xs text-muted-foreground">No linked lead</p>
          ) : leadLoading ? (
            <div className="space-y-2 rounded-lg border border-border bg-background p-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          ) : lead ? (
            <div className="space-y-1.5 rounded-lg border border-border bg-background p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-foreground">
                  {lead.lead_number}
                </span>
                <Badge variant={STATUS_VARIANTS[lead.status] || 'primary-soft'}>
                  {formatStatus(lead.status)}
                </Badge>
              </div>
              {serviceSuburb && (
                <p className="text-xs text-muted-foreground">{serviceSuburb}</p>
              )}
              {lead.quoted_price != null && (
                <p className="text-[15px] font-semibold tabular-nums text-foreground">
                  {formatCurrency(lead.quoted_price)}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Lead unavailable</p>
          )}
        </section>

        {/* Details */}
        <section className="space-y-2">
          <SectionEyebrow>Details</SectionEyebrow>
          <div className="space-y-1.5">
            {thread.contact_phone && <DetailRow label="Phone" value={thread.contact_phone} />}
            {thread.contact_email && <DetailRow label="Email" value={thread.contact_email} />}
            {thread.campaign_name && <DetailRow label="Campaign" value={thread.campaign_name} />}
            {thread.tracking_number && (
              <DetailRow label="Tracking no." value={thread.tracking_number} />
            )}
            <DetailRow
              label="Channel"
              value={thread.last_message_type === 'sms' ? 'SMS' : 'Email'}
            />
          </div>
        </section>

        {/* Recent activity */}
        {leadId && (
          <section className="space-y-2">
            <SectionEyebrow>Recent activity</SectionEyebrow>
            {activitiesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ) : !activities?.length ? (
              <p className="text-xs text-muted-foreground">No recent activity</p>
            ) : (
              <ul className="space-y-2.5">
                {activities.slice(0, 5).map((activity) => (
                  <li key={activity.id} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-xs text-foreground">
                        {activity.description}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </ScrollArea>
  );
}
